import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { 
  FileText, 
  Volume2, 
  Brain, 
  Database, 
  GitBranch,
  Play,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle
} from 'lucide-react';
import clsx from 'clsx';

const AiNode = ({ data, selected }) => {
  // ノードタイプに応じたアイコン
  const getNodeIcon = (nodeType) => {
    const iconMap = {
      text_processor: FileText,
      audio_converter: Volume2,
      whisper_transcribe: Volume2,
      gpt_processor: Brain,
      file_uploader: Database,
      condition_checker: GitBranch,
    };
    
    return iconMap[nodeType] || FileText;
  };

  // カテゴリーに応じた色
  const getCategoryColor = (nodeType) => {
    const categoryMap = {
      text_processor: 'text',
      audio_converter: 'audio',
      whisper_transcribe: 'ai',
      gpt_processor: 'ai',
      file_uploader: 'storage',
      condition_checker: 'logic',
    };
    
    return categoryMap[nodeType] || 'text';
  };

  // 実行状態に応じたスタイル
  const getExecutionStyle = (status) => {
    switch (status) {
      case 'running':
        return 'border-blue-400 shadow-execution animate-pulse';
      case 'completed':
        return 'border-green-400 shadow-lg';
      case 'failed':
        return 'border-red-400 shadow-lg';
      default:
        return '';
    }
  };

  // 実行状態アイコン
  const getExecutionIcon = (status) => {
    switch (status) {
      case 'running':
        return <Loader className="animate-spin" size={16} />;
      case 'completed':
        return <CheckCircle className="text-green-600" size={16} />;
      case 'failed':
        return <XCircle className="text-red-600" size={16} />;
      default:
        return null;
    }
  };

  const Icon = getNodeIcon(data.nodeType);
  const category = getCategoryColor(data.nodeType);
  const executionStatus = data.executionStatus;
  const progress = data.progress;

  return (
    <div
      className={clsx(
        'px-4 py-3 shadow-node rounded-lg border-2 bg-white min-w-[200px] transition-all duration-200',
        selected ? 'border-studio-400 shadow-node-hover' : 'border-gray-200',
        getExecutionStyle(executionStatus),
        `hover:shadow-node-hover`
      )}
    >
      {/* 入力ハンドル */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 border-2 border-gray-400 bg-white"
        style={{ left: -6 }}
      />

      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={clsx(
            'p-1.5 rounded-md',
            `bg-node-${category} bg-opacity-20`
          )}>
            <Icon 
              size={16} 
              className={clsx(`text-node-${category}`)}
            />
          </div>
          
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 text-sm truncate">
              {data.label}
            </h3>
          </div>
          
          {/* 実行状態アイコン */}
          {getExecutionIcon(executionStatus)}
        </div>
      </div>

      {/* ノード詳細情報 */}
      <div className="text-xs text-gray-600 space-y-1">
        {/* 入力数 */}
        {data.inputs && Object.keys(data.inputs).length > 0 && (
          <div>
            入力: {Object.keys(data.inputs).length}個
          </div>
        )}
        
        {/* 進捗表示 */}
        {progress && executionStatus === 'running' && (
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-blue-600">実行中</span>
              <span className="text-blue-600">{progress.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            {progress.message && (
              <div className="text-blue-600 truncate" title={progress.message}>
                {progress.message}
              </div>
            )}
          </div>
        )}
        
        {/* 完了状態 */}
        {executionStatus === 'completed' && (
          <div className="text-green-600 flex items-center space-x-1">
            <CheckCircle size={12} />
            <span>完了</span>
          </div>
        )}
        
        {/* エラー状態 */}
        {executionStatus === 'failed' && (
          <div className="text-red-600 flex items-center space-x-1">
            <AlertCircle size={12} />
            <span>エラー</span>
          </div>
        )}
      </div>

      {/* 設定表示 */}
      {data.config && Object.keys(data.config).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            設定済み
          </div>
        </div>
      )}

      {/* 出力ハンドル */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 border-2 border-gray-400 bg-white"
        style={{ right: -6 }}
      />

      {/* クイックアクション（ホバー時） */}
      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            // 実行アクション
          }}
          className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
          title="実行"
        >
          <Play size={12} />
        </button>
      </div>
    </div>
  );
};

export default memo(AiNode);